import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase_client";
import { useSession } from "../contexts/SessionContext";

export const MALAYSIA_STATES = [
  { Code: "01", State: "Johor" },
  { Code: "02", State: "Kedah" },
  { Code: "03", State: "Kelantan" },
  { Code: "04", State: "Melaka" },
  { Code: "05", State: "Negeri Sembilan" },
  { Code: "06", State: "Pahang" },
  { Code: "07", State: "Pulau Pinang" },
  { Code: "08", State: "Perak" },
  { Code: "09", State: "Perlis" },
  { Code: "10", State: "Selangor" },
  { Code: "11", State: "Terengganu" },
  { Code: "12", State: "Sabah" },
  { Code: "13", State: "Sarawak" },
  { Code: "14", State: "Wilayah Persekutuan Kuala Lumpur" },
  { Code: "15", State: "Wilayah Persekutuan Labuan" },
  { Code: "16", State: "Wilayah Persekutuan Putrajaya" },
  { Code: "17", State: "Not Applicable" },
] as const;

export type EInvoiceIdType = "BRN" | "NRIC" | "PASSPORT" | "ARMY";

export const ID_TYPE_LABELS: Record<EInvoiceIdType, string> = {
  BRN: "Business Registration No. (BRN)",
  NRIC: "MyKad / NRIC",
  PASSPORT: "Passport No.",
  ARMY: "Army / Police No.",
};

export interface EInvoiceFormData {
  owner_id: string;
  /** LHDN RegistrationName — official registered name of the buyer. */
  legal_name: string;
  /** Internal finance/billing contact person. Not sent to LHDN. */
  contact_name: string;
  /** LHDN Contact.ElectronicMail */
  email: string;
  /** LHDN Contact.Telephone — include country code e.g. +60123456789 */
  phone: string;
  tin: string;
  /** LHDN PartyIdentification schemeID="SST". Optional — blank is treated as "NA". */
  sst_registration: string;
  id_type: EInvoiceIdType;
  brn: string;           // holds the ID value regardless of id_type
  address_line_1: string;
  address_line_2: string;
  address_line_3: string;
  city: string;
  postal_code: string;
  state_code: string;
}

/** Payload that was written to `org_einvoice_details`, returned to the caller after a successful save. */
export interface EInvoiceSavedDetails extends EInvoiceFormData {
  org_id: string;
}

const initialEInvoiceForm: EInvoiceFormData = {
  owner_id: "",
  legal_name: "",
  contact_name: "",
  email: "",
  phone: "",
  tin: "",
  sst_registration: "",
  id_type: "BRN",
  brn: "",
  address_line_1: "",
  address_line_2: "",
  address_line_3: "",
  city: "",
  postal_code: "",
  state_code: "",
};

/** Derive the most likely idType from a TIN prefix. TINs starting with 'D' = individual. */
function deriveIdType(tin: string): EInvoiceIdType {
  return tin.trim().toUpperCase().startsWith("D") ? "NRIC" : "BRN";
}

// BRN: new SSM 12 digits (202101123456) or old SSM 6 digits + dash + 1 letter (123456-A)
// BRN accepts:
//   • Standard company: exactly 12 digits (e.g. 202101123456)
//   • Audit firm: AF followed by digits (e.g. AF001234, AF12345678)
//   • LLP / other registered entities: alphanumeric, 4–20 chars
const BRN_REGEX = /^(?:\d{12}|AF\d{4,10}|[A-Z0-9]{4,20})$/i;

export interface EInvoiceDetailsModalProps {
  /** Controls visibility. */
  open: boolean;
  /** Organization the e-invoice details belong to. */
  orgId: string | null | undefined;
  /** Organization name shown in the read-only "Organization" field. */
  orgName?: string | null;
  /** ISO country of the org; drives postal-code rules (MY = exactly 5 digits). */
  orgCountryIso?: string | null;
  /** Modal heading. */
  title?: string;
  /** Submit button label. */
  submitLabel?: string;
  onClose: () => void;
  /**
   * Called after the details are successfully saved to `org_einvoice_details`.
   * The caller performs its own follow-up (e.g. start payment, submit to LHDN).
   * The modal stays in its "saving" state until this resolves, then resets.
   * The caller is responsible for closing the modal (e.g. via `onClose`).
   */
  onSaved?: (details: EInvoiceSavedDetails) => void | Promise<void>;
  /** Called after the e-invoice details are successfully deleted. */
  onRemoved?: () => void | Promise<void>;
}

/**
 * Reusable "Buyer details for e-Invoice" modal.
 *
 * Owns the form, validation, and the upsert into `org_einvoice_details`.
 * It is intentionally agnostic about what happens next — the parent supplies an
 * `onSaved` callback to drive the purchase or LHDN-submit flow.
 */
export default function EInvoiceDetailsModal({
  open,
  orgId,
  orgName,
  orgCountryIso,
  title = "Buyer details ( For E-Invoice )",
  submitLabel = "Save & Apply",
  onClose,
  onSaved,
  onRemoved,
}: EInvoiceDetailsModalProps) {
  const { user } = useSession();
  const [form, setForm] = useState<EInvoiceFormData>(initialEInvoiceForm);
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [orgOwners, setOrgOwners] = useState<{ id: string; display_name: string }[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tinValidating, setTinValidating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (!open || !orgId) return;
    setFormLoading(true);
    (async () => {
      try {
        const [detailsRes, membersRes] = await Promise.all([
          supabase
            .from("org_einvoice_details")
            .select(
              "id, owner_id, legal_name, contact_name, email, phone, tin, sst_registration, id_type, brn, address_line_1, address_line_2, address_line_3, city, postal_code, is_default, state_code"
            )
            .eq("org_id", orgId)
            .maybeSingle(),
          supabase
            .from("organization_members")
            .select("user_id")
            .eq("org_id", orgId)
            .eq("role_id", 1)
            .eq("is_active", true),
        ]);

        const data = detailsRes.data;
        const ownerIds = (membersRes.data ?? []).map((m: { user_id: string }) => m.user_id);

        if (ownerIds.length > 0) {
          const { data: users } = await supabase
            .from("user_profiles")
            .select("id, display_name")
            .in("id", ownerIds);
          setOrgOwners(
            (users ?? []).map((u: { id: string; display_name: string | null }) => ({
              id: u.id,
              display_name: u.display_name || u.id.slice(0, 8),
            }))
          );
        } else {
          setOrgOwners([]);
        }

        setExistingId(data?.id ?? null);

        if (data) {
          const savedTin = data.tin ?? "";
          const savedIdType = (data.id_type as EInvoiceIdType) ?? deriveIdType(savedTin);
          setForm({
            owner_id: data.owner_id ?? ownerIds[0] ?? "",
            legal_name: data.legal_name ?? "",
            contact_name: data.contact_name ?? "",
            email: data.email ?? "",
            phone: data.phone ?? "",
            tin: savedTin,
            sst_registration: data.sst_registration ?? "",
            id_type: savedIdType,
            brn: data.brn ?? "",
            address_line_1: data.address_line_1 ?? "",
            address_line_2: data.address_line_2 ?? "",
            address_line_3: data.address_line_3 ?? "",
            city: data.city ?? "",
            postal_code: data.postal_code ?? "",
            state_code: data.state_code ?? "",
          });
        } else {
          setForm({
            ...initialEInvoiceForm,
            owner_id: ownerIds[0] ?? "",
          });
        }
      } finally {
        setFormLoading(false);
      }
    })();
  }, [open, orgId]);

  const handleClose = () => {
    setForm(initialEInvoiceForm);
    setErrors({});
    setTinValidating(false);
    setExistingId(null);
    setConfirmRemove(false);
    onClose();
  };

  const handleRemoveConfirmed = async () => {
    if (!existingId) return;
    setRemoving(true);
    try {
      await supabase.from("org_einvoice_details").delete().eq("id", existingId);
      await onRemoved?.();
      handleClose();
    } catch (err) {
      console.error(err);
      setConfirmRemove(false);
    } finally {
      setRemoving(false);
    }
  };

  /** Calls validate-lhdn-tin edge function using the form's id_type + brn as idValue. */
  const validateTinWithLhdn = async (): Promise<boolean> => {
    const tin = form.tin.trim();
    const idValue = form.brn.trim();
    const idType = form.id_type;

    if (!tin || !idValue) return false;

    // Skip LHDN call if user explicitly has no ID (NA for individuals)
    if (idValue === "NA") return true;

    setTinValidating(true);
    try {
      const { data, error } = await supabase.functions.invoke("validate-lhdn-tin", {
        body: { tin, idType, idValue },
      });
      if (error) {
        setErrors((prev) => ({ ...prev, tin: "Could not reach LHDN to validate TIN. Please try again." }));
        return false;
      }
      if (!data?.valid) {
        setErrors((prev) => ({
          ...prev,
          tin: data?.error ?? `TIN and ${ID_TYPE_LABELS[idType]} do not match LHDN records. Please verify both are correct and belong to the same taxpayer.`,
        }));
        return false;
      }
      return true;
    } finally {
      setTinValidating(false);
    }
  };

  const UPPERCASE_FIELDS: (keyof EInvoiceFormData)[] = [
    "legal_name", "address_line_1", "address_line_2", "address_line_3", "city",
  ];

  const handleFormChange = (field: keyof EInvoiceFormData, value: string) => {
    const normalised = UPPERCASE_FIELDS.includes(field) ? value.toUpperCase() : value;
    setForm((prev) => {
      const next = { ...prev, [field]: normalised };
      // Auto-suggest id_type when TIN changes and user hasn't manually overridden
      if (field === "tin" && typeof value === "string") {
        next.id_type = deriveIdType(value);
      }
      return next;
    });
  };

  const validate = (): Record<string, string> => {
    const err: Record<string, string> = {};
    const ADDRESS_MAX = 150;
    const CITY_MAX = 50;

    // ---- Legal name (LHDN RegistrationName) ----
    const legalName = form.legal_name.trim();
    if (!legalName) {
      err.legal_name = "Firm / registered name is required";
    } else if (legalName.length > 200) {
      err.legal_name = "Max 200 characters";
    }

    // ---- Contact name (internal, optional but encouraged) ----
    if (form.contact_name.length > 100) {
      err.contact_name = "Max 100 characters";
    }

    // ---- Email (LHDN Contact.ElectronicMail) ----
    const email = form.email.trim();
    if (!email) {
      err.email = "Finance email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      err.email = "Invalid email address (e.g. finance@company.com)";
    }

    // ---- Phone (LHDN Contact.Telephone) ----
    // Only digits and leading +, no spaces or dashes allowed.
    const phone = form.phone.trim();
    if (!phone) {
      err.phone = "Finance phone is required";
    } else if (!/^\+[0-9]{7,15}$/.test(phone)) {
      err.phone = "Use digits only with country code, no spaces or dashes (e.g. +60123456789)";
    }

    // ---- TIN ----
    // Format is not enforced here — digit-only TINs are valid, and LHDN
    // validate-lhdn-tin is the source of truth on save.
    const tin = form.tin.trim();
    if (!tin) {
      err.tin = "TIN is required";
    }

    // ---- ID Value (label depends on id_type) ----
    const brn = form.brn.trim();
    const idType = form.id_type;

    if (!brn) {
      err.brn = `${ID_TYPE_LABELS[idType]} is required`;
    } else {
      if (idType === "BRN") {
        if (!BRN_REGEX.test(brn)) {
          err.brn = "Invalid BRN. Accepted formats: 12-digit company number (202101123456), audit firm (AF001234), or alphanumeric 4–20 chars";
        }
      } else if (idType === "NRIC") {
        if (!/^\d{12}$/.test(brn)) {
          err.brn = "Invalid NRIC. Must be exactly 12 digits with no dashes";
        }
      } else if (idType === "PASSPORT") {
        if (brn.length < 6 || brn.length > 20) {
          err.brn = "Passport number must be 6–20 characters";
        }
      } else if (idType === "ARMY") {
        if (brn.length < 4 || brn.length > 20) {
          err.brn = "Army/police number must be 4–20 characters";
        }
      }
    }

    // ---- Address line 1 ----
    const addr1 = form.address_line_1.trim();
    if (!addr1) {
      err.address_line_1 = "Address line 1 is required";
    } else if (addr1.length > ADDRESS_MAX) {
      err.address_line_1 = `Max ${ADDRESS_MAX} characters`;
    }
    if (form.address_line_2.length > ADDRESS_MAX) err.address_line_2 = `Max ${ADDRESS_MAX} characters`;
    if (form.address_line_3.length > ADDRESS_MAX) err.address_line_3 = `Max ${ADDRESS_MAX} characters`;

    // ---- City ----
    const city = form.city.trim();
    if (!city) {
      err.city = "City is required";
    } else if (city.length > CITY_MAX) {
      err.city = `Max ${CITY_MAX} characters`;
    }

    // ---- Postal code (MY: 5 digits) ----
    const postal = form.postal_code.trim();
    if (!postal) {
      err.postal_code = "Postal code is required (5 digits for Malaysia)";
    } else if (!/^\d{5}$/.test(postal)) {
      err.postal_code = "Must be exactly 5 digits for Malaysia";
    }

    // ---- State ----
    const state = form.state_code.trim();
    if (!state) {
      err.state_code = "State is required";
    }

    return err;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId || !user?.id) return;

    // 1. Local format validation first
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    // 2. LHDN live TIN validation (only if TIN + BRN are filled)
    if (form.tin.trim() && form.brn.trim()) {
      const tinOk = await validateTinWithLhdn();
      if (!tinOk) return;
    }

    setSaving(true);
    try {
      const { data: existing } = await supabase
        .from("org_einvoice_details")
        .select("id")
        .eq("org_id", orgId)
        .maybeSingle();

      const payload = {
        org_id: orgId,
        owner_id: form.owner_id || (orgOwners[0]?.id ?? user.id),
        legal_name: form.legal_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        tin: form.tin.trim(),
        sst_registration: form.sst_registration.trim(),
        id_type: form.id_type,
        brn: form.brn.trim(),
        address_line_1: form.address_line_1.trim(),
        address_line_2: form.address_line_2.trim(),
        address_line_3: form.address_line_3.trim(),
        city: form.city.trim(),
        postal_code: form.postal_code.trim(),
        is_default: true,
        updated_at: new Date().toISOString(),
        state_code: form.state_code.trim(),
      };

      if (existing) {
        await supabase.from("org_einvoice_details").update(payload).eq("id", existing.id);
      } else {
        await supabase.from("org_einvoice_details").insert({
          ...payload,
          created_by: user.id,
        });
      }

      await onSaved?.(payload as EInvoiceSavedDetails);
    } catch (err) {
      console.error(err);
      alert("Failed to save e-invoice details.");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !orgId) return null;

  const inputCls = (field: string) =>
    `w-full border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${
      errors[field] ? "border-red-500" : "border-gray-300"
    }`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      {/* flex-col so header + footer are sticky and only the body scrolls */}
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* ── Header (fixed) ── */}
        <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">

            {/* ── Scrollable body ── */}
            <div className="flex-1 overflow-y-auto px-5 py-4 relative">
              {/* Loading overlay — sits on top of the pre-rendered form so modal size never changes */}
              {formLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <svg className="w-4 h-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Loading details...
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">

                {/* ── Left: identity + TIN ── */}
                <div className="space-y-3">

                  {/* Firm name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Firm / Registered Name <span className="text-red-500">*</span>
                    </label>
                    <input type="text" value={form.legal_name}
                      onChange={(e) => { handleFormChange("legal_name", e.target.value); if (errors.legal_name) setErrors(p => ({ ...p, legal_name: "" })); }}
                      className={inputCls("legal_name")} placeholder="As registered with SSM / LHDN" maxLength={200} />
                    {errors.legal_name && <p className="mt-0.5 text-xs text-red-600">{errors.legal_name}</p>}
                  </div>

                  {/* Finance email — full width now that contact name is removed */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Finance Email <span className="text-red-500">*</span>
                    </label>
                    <input type="email" value={form.email}
                      onChange={(e) => { handleFormChange("email", e.target.value); if (errors.email) setErrors(p => ({ ...p, email: "" })); }}
                      className={inputCls("email")} placeholder="finance@company.com" maxLength={100} />
                    {errors.email && <p className="mt-0.5 text-xs text-red-600">{errors.email}</p>}
                  </div>

                  {/* Phone — full row */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Finance Phone <span className="text-red-500">*</span>
                    </label>
                    <input type="tel" value={form.phone}
                      onChange={(e) => { handleFormChange("phone", e.target.value); if (errors.phone) setErrors(p => ({ ...p, phone: "" })); }}
                      className={inputCls("phone")} placeholder="+60123456789 (no spaces/dashes)" maxLength={20} />
                    {errors.phone && <p className="mt-0.5 text-xs text-red-600">{errors.phone}</p>}
                  </div>

                  {/* TIN + SST side-by-side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        TIN <span className="text-red-500">*</span>
                      </label>
                      <input type="text" value={form.tin}
                        onChange={(e) => { handleFormChange("tin", e.target.value); if (errors.tin) setErrors(p => ({ ...p, tin: "" })); }}
                        className={inputCls("tin")} placeholder="e.g. C1234567890" maxLength={35} />
                      {errors.tin && <p className="mt-0.5 text-xs text-red-600">{errors.tin}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        SST Registration <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input type="text" value={form.sst_registration}
                        onChange={(e) => handleFormChange("sst_registration", e.target.value)}
                        className={inputCls("sst_registration")} placeholder="Leave blank if none" maxLength={50} />
                    </div>
                  </div>

                  {/* ID Type + ID Value — joined like phone country prefix */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      ID Type &amp; Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex">
                      <select
                        value={form.id_type}
                        onChange={(e) => handleFormChange("id_type", e.target.value as EInvoiceIdType)}
                        className="shrink-0 border border-gray-300 border-r-0 rounded-l-lg px-2 py-1.5 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        style={{ width: "7.5rem" }}
                      >
                        <option value="BRN">BRN</option>
                        <option value="NRIC">NRIC</option>
                        <option value="PASSPORT">Passport</option>
                        <option value="ARMY">Army</option>
                      </select>
                      <input
                        type="text"
                        value={form.brn}
                        onChange={(e) => { handleFormChange("brn", e.target.value); if (errors.brn) setErrors(p => ({ ...p, brn: "" })); }}
                        className={`flex-1 border rounded-r-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${errors.brn ? "border-red-500" : "border-gray-300"}`}
                        placeholder={
                          form.id_type === "BRN" ? "12 digits e.g. 202101123456" :
                          form.id_type === "NRIC" ? "12 digits, no dashes" :
                          form.id_type === "PASSPORT" ? "e.g. A12345678" : "Army / police ID"
                        }
                        maxLength={50}
                      />
                    </div>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {form.id_type === "BRN" ? "Company / business account" : "Individual / freelancer account"}
                    </p>
                    {errors.brn && <p className="mt-0.5 text-xs text-red-600">{errors.brn}</p>}
                  </div>

                </div>

                {/* ── Right: address ── */}
                <div className="space-y-3">

                  {/* Address line 1 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Address line 1 <span className="text-red-500">*</span>
                    </label>
                    <input type="text" value={form.address_line_1}
                      onChange={(e) => { handleFormChange("address_line_1", e.target.value); if (errors.address_line_1) setErrors(p => ({ ...p, address_line_1: "" })); }}
                      className={inputCls("address_line_1")} maxLength={150} />
                    {errors.address_line_1 && <p className="mt-0.5 text-xs text-red-600">{errors.address_line_1}</p>}
                  </div>

                  {/* Address line 2 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Address line 2</label>
                    <input type="text" value={form.address_line_2}
                      onChange={(e) => { handleFormChange("address_line_2", e.target.value); if (errors.address_line_2) setErrors(p => ({ ...p, address_line_2: "" })); }}
                      className={inputCls("address_line_2")} maxLength={150} />
                    {errors.address_line_2 && <p className="mt-0.5 text-xs text-red-600">{errors.address_line_2}</p>}
                  </div>

                  {/* Address line 3 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Address line 3</label>
                    <input type="text" value={form.address_line_3}
                      onChange={(e) => { handleFormChange("address_line_3", e.target.value); if (errors.address_line_3) setErrors(p => ({ ...p, address_line_3: "" })); }}
                      className={inputCls("address_line_3")} maxLength={150} />
                    {errors.address_line_3 && <p className="mt-0.5 text-xs text-red-600">{errors.address_line_3}</p>}
                  </div>

                  {/* City — full width */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input type="text" value={form.city}
                      onChange={(e) => { handleFormChange("city", e.target.value); if (errors.city) setErrors(p => ({ ...p, city: "" })); }}
                      className={inputCls("city")} maxLength={50} />
                    {errors.city && <p className="mt-0.5 text-xs text-red-600">{errors.city}</p>}
                  </div>

                  {/* Postal + State side-by-side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Postal Code <span className="text-red-500">*</span>
                      </label>
                      <input type="text" value={form.postal_code}
                        onChange={(e) => { handleFormChange("postal_code", e.target.value); if (errors.postal_code) setErrors(p => ({ ...p, postal_code: "" })); }}
                        className={inputCls("postal_code")}
                        placeholder={orgCountryIso === "MY" ? "5 digits" : ""}
                        maxLength={orgCountryIso === "MY" ? 5 : 50} />
                      {errors.postal_code && <p className="mt-0.5 text-xs text-red-600">{errors.postal_code}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        State <span className="text-red-500">*</span>
                      </label>
                      <select value={form.state_code}
                        onChange={(e) => { handleFormChange("state_code", e.target.value); if (errors.state_code) setErrors(p => ({ ...p, state_code: "" })); }}
                        className={`w-full border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${errors.state_code ? "border-red-500" : "border-gray-300"}`}>
                        <option value="">Select state</option>
                        {MALAYSIA_STATES.map((s) => (
                          <option key={s.Code} value={s.Code}>{s.State.toUpperCase()}</option>
                        ))}
                      </select>
                      {errors.state_code && <p className="mt-0.5 text-xs text-red-600">{errors.state_code}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Footer (sticky, always visible) ── */}
            <div className="border-t bg-white shrink-0">
              {/* Inline remove confirmation panel */}
              {confirmRemove && (
                <div className="flex items-start gap-3 px-5 py-3 bg-red-50 border-b border-red-100">
                  <svg className="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-red-700 mb-0.5">Remove e-invoice details?</p>
                    <p className="text-xs text-red-600">
                      Once removed, future invoices will revert to standard (non-LHDN) format.
                      You can re-enable e-invoice at any time by setting up your details again.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setConfirmRemove(false)}
                      disabled={removing}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveConfirmed}
                      disabled={removing}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 text-white bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-50"
                    >
                      {removing ? "Removing..." : "Yes, Remove"}
                    </button>
                  </div>
                </div>
              )}

              {/* Main footer actions */}
              <div className="flex items-center justify-between gap-2 px-5 py-3">
                {/* Left: remove trigger */}
                <div>
                  {existingId && !confirmRemove && (
                    <button
                      type="button"
                      onClick={() => setConfirmRemove(true)}
                      disabled={removing || saving || tinValidating}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove Address
                    </button>
                  )}
                </div>
                {/* Right: cancel + save */}
                <div className="flex gap-2">
                  <button type="button" onClick={handleClose} className="px-4 py-2 text-sm f-btn f-btn-soft">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving || tinValidating || confirmRemove} className="px-4 py-2 text-sm f-btn f-btn-success">
                    {tinValidating ? "Validating TIN..." : saving ? "Saving..." : submitLabel}
                  </button>
                </div>
              </div>
            </div>

          </form>
      </div>
    </div>
  );
}
